import { Feature, FeatureCollection } from "geojson";
import { ExpressionSpecification } from "mapbox-gl";

/**
 * Map Labels
 */
export const MAP_LABEL_FONT: ExpressionSpecification = [
	"Open Sans Bold",
	"Arial Unicode MS Bold",
];
export const MAP_LABEL_DEFAULT_SIZE = 14;
export const MAP_LABEL_DEFAULT_PAINT = {
	"text-color": "#fff",
	"text-halo-color": "#000",
	"text-halo-width": 1,
};

/**
 * Clusters
 */
export const CLUSTER_COLOR_FEW = "#51bbd6";
export const CLUSTER_COLOR_MEDIUM = "#f1f075";
export const CLUSTER_COLOR_MANY = "#f28cb1";

export const DEFAULT_CLUSTER_COLOR_SETTINGS: ExpressionSpecification = [
	"step",
	["get", "point_count"],
	"#173f5f",
	100,
	"#20639b",
	750,
	"#e3553b",
];

export const DEFAULT_CLUSTER_RADIUS_SETTINGS: ExpressionSpecification = [
	"step",
	["get", "point_count"],
	20,
	100,
	30,
	750,
	35,
];

export const DEFAULT_CLUSTER_PAINT_SETTINGS = {
	"circle-color": DEFAULT_CLUSTER_COLOR_SETTINGS,
	"circle-radius": DEFAULT_CLUSTER_RADIUS_SETTINGS,
};

export const DEFAULT_CLUSTER_COUNT_SIZE = 13;
export const DEFAULT_CLUSTER_COUNT_LAYOUT = {
	"text-field": ["get", "point_count_abbreviated"] as ExpressionSpecification,
	"text-font": MAP_LABEL_FONT,
	"text-size": DEFAULT_CLUSTER_COUNT_SIZE,
};

export const DEFAULT_CLUSTER_COUNT_PAINT = {
	"text-color": "#fff",
};

export const EMPTY_GEOJSON_FEATURE_COLLECTION = {
	type: "FeatureCollection",
	features: [],
} as const as FeatureCollection;

export const EMPTY_GEOJSON_FEATURE = {
	type: "Feature",
	geometry: null,
	properties: {},
} as const as Feature<null>;

// DRAWING STYLES
export const BASE_POINT_STYLES = {
	radius: 6,
	strokeWidth: 2,
	strokeColor: "#ffffff",
};

export const BASE_POLYGON_STYLES = {
	strokeWidth: 2,
	fillOpacity: 0.3,
};

export const DRAWING_COLORS = {
	drawing: "#1e40af", // Blue for drawing
	display: "#dc2626", // Red for display
	active: "#fbbf24", // Amber for active states
	white: "#ffffff",
};

export const GL_DRAW_DRAWING_STYLES = [
	// Point styling - make points bigger
	{
		id: "gl-draw-point-point-stroke-inactive",
		type: "circle",
		filter: [
			"all",
			["==", "active", "false"],
			["==", "$type", "Point"],
			["==", "meta", "feature"],
			["!=", "mode", "static"],
		],
		paint: {
			"circle-radius": 8, // Increased from default 5
			"circle-opacity": 1,
			"circle-color": DRAWING_COLORS.white,
		},
	},
	{
		id: "gl-draw-point-point-inactive",
		type: "circle",
		filter: [
			"all",
			["==", "active", "false"],
			["==", "$type", "Point"],
			["==", "meta", "feature"],
			["!=", "mode", "static"],
		],
		paint: {
			"circle-radius": 6, // Increased from default 3
			"circle-color": DRAWING_COLORS.drawing,
		},
	},
	// Active point styling - when selected
	{
		id: "gl-draw-point-point-stroke-active",
		type: "circle",
		filter: [
			"all",
			["==", "active", "true"],
			["==", "$type", "Point"],
			["==", "meta", "feature"],
		],
		paint: {
			"circle-radius": 9, // Increased from default 6
			"circle-color": DRAWING_COLORS.white,
		},
	},
	{
		id: "gl-draw-point-point-active",
		type: "circle",
		filter: [
			"all",
			["==", "active", "true"],
			["==", "$type", "Point"],
			["==", "meta", "feature"],
		],
		paint: {
			"circle-radius": 7, // Increased from default 4
			"circle-color": DRAWING_COLORS.active,
		},
	},
	// Polygon fill
	{
		id: "gl-draw-polygon-fill-inactive",
		type: "fill",
		filter: [
			"all",
			["==", "active", "false"],
			["==", "$type", "Polygon"],
			["!=", "mode", "static"],
		],
		paint: {
			"fill-color": DRAWING_COLORS.drawing,
			"fill-outline-color": DRAWING_COLORS.drawing,
			"fill-opacity": BASE_POLYGON_STYLES.fillOpacity,
		},
	},
	{
		id: "gl-draw-polygon-fill-active",
		type: "fill",
		filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
		paint: {
			"fill-color": DRAWING_COLORS.active,
			"fill-outline-color": DRAWING_COLORS.active,
			"fill-opacity": BASE_POLYGON_STYLES.fillOpacity,
		},
	},
	// Polygon stroke
	{
		id: "gl-draw-polygon-stroke-inactive",
		type: "line",
		filter: [
			"all",
			["==", "active", "false"],
			["==", "$type", "Polygon"],
			["!=", "mode", "static"],
		],
		layout: {
			"line-cap": "round",
			"line-join": "round",
		},
		paint: {
			"line-color": DRAWING_COLORS.drawing,
			"line-width": BASE_POLYGON_STYLES.strokeWidth,
		},
	},
	{
		id: "gl-draw-polygon-stroke-active",
		type: "line",
		filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
		layout: {
			"line-cap": "round",
			"line-join": "round",
		},
		paint: {
			"line-color": DRAWING_COLORS.active,
			"line-width": BASE_POLYGON_STYLES.strokeWidth,
		},
	},
	// Polygon vertices (corner points) when editing
	{
		id: "gl-draw-polygon-and-line-vertex-stroke-inactive",
		type: "circle",
		filter: [
			"all",
			["==", "meta", "vertex"],
			["==", "$type", "Point"],
			["!=", "mode", "static"],
		],
		paint: {
			"circle-radius": 6,
			"circle-color": DRAWING_COLORS.white,
		},
	},
	{
		id: "gl-draw-polygon-and-line-vertex-inactive",
		type: "circle",
		filter: [
			"all",
			["==", "meta", "vertex"],
			["==", "$type", "Point"],
			["!=", "mode", "static"],
		],
		paint: {
			"circle-radius": 4,
			"circle-color": DRAWING_COLORS.active,
		},
	},
	// CRITICAL STYLES FOR MIDPOINTS - These allow edge splitting
	{
		id: "gl-draw-polygon-midpoint",
		type: "circle",
		filter: ["all", ["==", "$type", "Point"], ["==", "meta", "midpoint"]],
		paint: {
			"circle-radius": 4,
			"circle-color": DRAWING_COLORS.active,
		},
	},
	{
		id: "gl-draw-polygon-and-line-vertex-halo-active",
		type: "circle",
		filter: [
			"all",
			["==", "active", "true"],
			["==", "meta", "vertex"],
			["==", "$type", "Point"],
		],
		paint: {
			"circle-radius": 8,
			"circle-color": DRAWING_COLORS.white,
		},
	},
	{
		id: "gl-draw-polygon-and-line-vertex-active",
		type: "circle",
		filter: [
			"all",
			["==", "active", "true"],
			["==", "meta", "vertex"],
			["==", "$type", "Point"],
		],
		paint: {
			"circle-radius": 6,
			"circle-color": DRAWING_COLORS.active,
		},
	},
	// Line string styles (needed for polygon drawing state)
	{
		id: "gl-draw-line-inactive",
		type: "line",
		filter: [
			"all",
			["==", "active", "false"],
			["==", "$type", "LineString"],
			["!=", "mode", "static"],
		],
		layout: {
			"line-cap": "round",
			"line-join": "round",
		},
		paint: {
			"line-color": DRAWING_COLORS.drawing,
			"line-width": BASE_POLYGON_STYLES.strokeWidth,
		},
	},
	{
		id: "gl-draw-line-active",
		type: "line",
		filter: [
			"all",
			["==", "$type", "LineString"],
			["==", "active", "true"],
		],
		layout: {
			"line-cap": "round",
			"line-join": "round",
		},
		paint: {
			"line-color": DRAWING_COLORS.active,
			"line-width": BASE_POLYGON_STYLES.strokeWidth,
		},
	},
];

export const GL_DRAW_DISPLAY_STYLES = [
	// Point styles - display mode (static)
	{
		id: "gl-draw-point-static",
		type: "circle",
		filter: ["all", ["==", "$type", "Point"], ["==", "mode", "static"]],
		paint: {
			"circle-radius": BASE_POINT_STYLES.radius,
			"circle-color": DRAWING_COLORS.display,
			"circle-stroke-width": BASE_POINT_STYLES.strokeWidth,
			"circle-stroke-color": BASE_POINT_STYLES.strokeColor,
		},
	},
	{
		id: "gl-draw-polygon-fill-static",
		type: "fill",
		filter: ["all", ["==", "$type", "Polygon"], ["==", "mode", "static"]],
		paint: {
			"fill-color": DRAWING_COLORS.display,
			"fill-opacity": BASE_POLYGON_STYLES.fillOpacity,
		},
	},
	{
		id: "gl-draw-polygon-stroke-static",
		type: "line",
		filter: ["all", ["==", "$type", "Polygon"], ["==", "mode", "static"]],
		layout: {
			"line-cap": "round",
			"line-join": "round",
		},
		paint: {
			"line-color": DRAWING_COLORS.display,
			"line-width": BASE_POLYGON_STYLES.strokeWidth,
		},
	},
];
